import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import Table from "@components/Table";

import { Box, Container } from "@mui/material";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import ButtonBackPage from "@components/ButtonBackPage";
import BackgroundImageCst from "@components/BackgroundImageCst";
import { ActionsType } from "src/interfaces/Common";
import { useParams } from "react-router-dom";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { ServicesURL } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useAuth } from "@context/auth";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useFilters } from "@context/filters";
import { Discussions, GetClusterData, ListGetClusterData } from "./types";
import CustomTopToolbar from "@components/Table/components/CustomTopToolbar";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import { useFetchDetail } from "@hooks/useFetchDetail";
import useSWR from "swr";

export function useFetchDiscussions(
  discussions: { clusterId: number; drupalId?: number }[],
  corporateId: number,
  initiativeId: number,
) {
  const { data: dataTable } = useSWR(
    discussions.length > 0
      ? ["discussions", ...discussions.map(({ clusterId }) => clusterId)]
      : null,
    async () => {
      const responses = await Promise.allSettled(
        discussions.map(async ({ clusterId, drupalId }) => {
          const res = await fetcher<{
            body: any;
            method: MethodType;
            endpoint: string;
            initiativeid: number;
            corporateid: number;
            clusterid: number;
            drupalid?: number;
          }>(
            `${ServicesURL.postWrapper}?initiativeid=${initiativeId}`,
            MethodType.POST,
            {
              body: {},
              method: MethodType.GET,
              endpoint: `${ServicesURL.getForum}`,
              initiativeid: initiativeId,
              clusterid: clusterId,
              drupalid: drupalId,
              corporateid: corporateId,
            },
          );
          return {
            clusterId,
            response: res as Discussions,
          };
        }),
      );
      const discussionRes = responses
        .map((result) => {
          if (result.status === "fulfilled") {
            return result.value;
          }
          return undefined;
        })
        .filter((v) => !!v);
      return discussionRes;
    },
  );
  return dataTable;
}

const Clusters: React.FC<{ page: PagesID }> = ({ page }) => {
  const { id, initiativeId } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${page + initiativeId}`;
  const [idClusterToDelete, setIdClusterToDelete] = useState<number>();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const {
    columns,
    actionsTopToolbar,
    actionForRow,
    dataTable,
    mutate,
    error,
    isLoading,
  } = useFetchTable(
    ServicesURL.getCommunityClusters
      .replace(":corporateId", id ?? "1")
      .replace(":initiativeId", initiativeId ?? ""),
    PagesID["corporate.initiative.community-cluster"],
    filters[filterId],
  );

  const discussionIds =
    (dataTable as ListGetClusterData | undefined)?.output.map((cluster) => ({
      clusterId: cluster.cluster.id,
      drupalId: cluster.cluster.drupal_id,
    })) ?? [];
  const discussions = useFetchDiscussions(discussionIds, +id!, +initiativeId!);
  const totals = discussions?.reduce<{ [discussionId: number]: number }>(
    (agg, cur) => {
      return { ...agg, [cur!.clusterId]: cur!.response.pager.total_elements };
    },
    {},
  );
  console.info(discussionIds, discussions, totals);

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID["corporate.initiative.community-cluster"],
  );

  const { trigger: triggerDelete } = useSWRMutation(
    ServicesURL.deleteCommunityCluster
      .replace(":corporateId", id ?? "1")
      .replace(":initiativeId", initiativeId ?? ""),
    (url: string, arg: { arg: any }) =>
      fetcher<any>(
        `${url.replace(":clusterId", idClusterToDelete ? `${idClusterToDelete}` : "")}`,
        MethodType.DELETE,
        arg.arg,
      ),
  );

  const handleDelete = React.useCallback(async () => {
    if (idClusterToDelete) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDelete();
        const newData = [
          ...(dataTable as ListGetClusterData).output.filter(
            (el: GetClusterData) => el.cluster.id !== idClusterToDelete,
          ),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [id, dataTable, idClusterToDelete, triggerDelete]);

  const { t } = useTranslation(["community-cluster-page"]);

  const actionsRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: number) => {
              setIdClusterToDelete(id);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: ActionsType[] =
      actionsRow &&
      [...actionsRow].map((action) => {
        if (action.permission === "corporate.initiative.discussion") {
          action.disabled = !row.original.is_talk_enabled;
        }
        return action;
      });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const dataMapper = (dataTableObj?: ListGetClusterData) => {
    return (
      dataTableObj?.output.map((item) => {
        return {
          name: item.cluster.name,
          id: item.cluster.id,
          cluster_id: item.cluster.id,
          owners: (item.owners ?? []).map(
            (owner) => `${owner.name ?? ""} ${owner.surname ?? ""}`,
          ),
          talks: totals?.[item.cluster.id],
          is_talk_enabled: item.cluster.is_talk_enabled,
        };
      }) ?? []
    );
  };

  const customRenderTopToolbar = ({
    table,
  }: {
    table: any;
  }): React.ReactNode => {
    return columns ? (
      <CustomTopToolbar
        id={page + initiativeId}
        key={`custom_top_toolbar_${page}`}
        columns={columns
          .filter((column) => (column as any)["hasFilter"])
          .map((column) => ({
            ...column,
            visibility: true,
            visibilityInTable: true,
          }))}
        configurationTopToolbar={actionsTopToolbar}
        table={table}
        hiddenFilters={false}
        resetFormFilters={undefined}
        setHideHeader={undefined}
        counterElements={false}
        onToolbarChange={() => {}}
        modalTitle={undefined}
        noSummarySelected={undefined}
        filtersException={undefined}
        enableFullScreenToggle={false}
      />
    ) : (
      <></>
    );
  };

  return (
    <>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-community.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${dataDetail?.name} - ${t("hero-title")}`}
          description={""}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          {isLoading ? (
            <FormLoadingStateCst />
          ) : (
            <Table
              id={page + initiativeId}
              data={dataMapper(dataTable)}
              columns={columns || []}
              configurationTopToolbar={actionsTopToolbar}
              configurationActionForRow={actionForRow}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.total_elements}
              renderTopToolbar={customRenderTopToolbar}
            />
          )}
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!idClusterToDelete}
        routeToSamePage={true}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        onCallBackClosed={() => {
          setIdClusterToDelete(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => {
          handleDelete();
        }}
        status={openToast}
      />
    </>
  );
};

export default Clusters;
